<script setup>
import TextInput from "../inputs/TextInput.vue";
import BoutonAction from "../inputs/BoutonAction";
import { useStore } from "vuex";
import {
  groupeDeTrois,
  debounce,
  getSousEtapeFromName,
  getTrackingObject,
} from "../../helpers";
import { reactive, computed, onMounted, ref, inject } from "vue";
import useGtmEvent from "../../use/useGtm";
import { useRouter, useRoute } from "vue-router";
import { canBeCommandePublique, isCommandePublique } from "@/domain/entreprise/helpers/commandePublique";

const store = useStore();
const router = useRouter();
const route = useRoute();
const { gtmEvent, gtmEventv4 } = useGtmEvent();
const $axiosClient = inject("$axiosClient");

const emit = defineEmits(["delete"]);
const dataRaisonSocialField = reactive({
  raisonSocialeEntreprise: "",
  focused: false,
  timer: null,
});
const errors = ref([]);

const opportunite = computed(() => store.getters.opportunite);
const suggestions = computed(() => store.getters.suggestions);
const departements = computed(() => store.state.departements);
const isEntrepriseSuggeree = computed(() => store.state.isEntrepriseSuggeree);

const isValid = computed(() => {
  return errors.value.length === 0;
});

const departementSelectionne = ref(null);

function handleInputRaisonSociale() {
  if (dataRaisonSocialField.raisonSocialeEntreprise.length <= 2) return;
  getPropositions();

  debounce(
    store.commit("UPDATE_OPPORTUNITE", {
      path: ["entreprise", "raisonSociale"],
      valeur: dataRaisonSocialField.raisonSocialeEntreprise,
    }),
  );
}
const getPropositions = debounce(() =>
  store.dispatch(
    "getSuggestionEntreprise",
    dataRaisonSocialField.raisonSocialeEntreprise,
  ),
);

function handleSelectionDepartement(e) {
  if (opportunite.value.entreprise.siren || e.target.value === "-") {
    vidangeEntrepriseOpportunite();
  }
  if (e.target.value !== "-") {
    const departementChoisi = departements.value.find(
      (d) => d.numero === e.target.value,
    );
    const nomDepartementSelectionne = `${departementChoisi.numero}-${departementChoisi.nom}`;
    if (departementChoisi.arrondissement) {
      store.commit("UPDATE_OPPORTUNITE", {
        path: ["entreprise", "siege", "adresse", "codePostal"],
        valeur: departementChoisi.numero,
      });
    } else {
      store.commit("UPDATE_OPPORTUNITE", {
        path: ["entreprise", "departement"],
        valeur: nomDepartementSelectionne,
      });
    }
    departementSelectionne.value = nomDepartementSelectionne;
  }
  check();
}
function vidangeEntrepriseOpportunite() {
  store.dispatch("setIsEntrepriseSuggeree", false);
  store.commit("UPDATE_OPPORTUNITE", {
    path: ["entreprise", "siren"],
    valeur: null,
  });
  store.commit("UPDATE_OPPORTUNITE", {
    path: ["entreprise", "codeNaf"],
    valeur: null,
  });
  store.commit("UPDATE_OPPORTUNITE", {
    path: ["entreprise", "departement"],
    valeur: null,
  });
  store.commit("UPDATE_OPPORTUNITE", {
    path: ["entreprise", "siege", "adresse", "codePostal"],
    valeur: null,
  });
  departementSelectionne.value = null;
}
function updateValue(suggestion) {
  store.commit("UPDATE_OPPORTUNITE", {
    path: ["entreprise", "siren"],
    valeur: suggestion.siren.valeur,
  });

  store.commit("UPDATE_OPPORTUNITE", {
    path: ["entreprise", "raisonSociale"],
    valeur: suggestion.raisonSociale,
  });

  store.commit("UPDATE_OPPORTUNITE", {
    path: ["entreprise", "codeNaf"],
    valeur: suggestion.codeNaf,
  });
  dataRaisonSocialField.raisonSocialeEntreprise = suggestion.raisonSociale;
  onBlur();
}
function isEntrepriseSelectionne(entreprise) {
  if (
    !opportunite?.value?.entreprise?.siren?.length ||
        !entreprise?.siren?.valeur
  )
    return false;
  return entreprise.siren.valeur === opportunite?.value?.entreprise?.siren;
}
function onFocus() {
  dataRaisonSocialField.focused = true;
}
function onBlur() {
  dataRaisonSocialField.focused = false;
}
async function check() {
  errors.value = [];

  if (
    opportunite.value.entreprise.siren === null &&
        departementSelectionne.value === null
  ) {
    errors.value = ["Vous devez séléctionner une valeur"];
  }
}
function selectionEntreprise(suggestion) {
  updateValue(suggestion);
  check();
  store.dispatch("setIsEntrepriseSuggeree", true);
  store.dispatch("postSirenEnCoursFormulaire", suggestion.siren.libelle);

  gtmEvent(
    "leads-formulaire",
    "professionnel-choix-raison-sociale",
    `${suggestion.siren.libelle} | ${suggestion.codeNaf}`,
    true,
  );
}

async function demarrer() {
  await check();
  if (!isValid.value) return;
  if (
    opportunite.value?.entreprise?.siren &&
        isEntrepriseSuggeree.value === true
  ) {
    $axiosClient
      .get(
        `/api/v1/entreprise/${opportunite.value.entreprise.siren}/profil`,
      )
      .then((response) => {
        opportunite.value.entreprise = response.data.entreprise;
        const groupeActivitesTags = response.data.groupeActivitesTags;
        const codeActiviteJuridique = response.data.codeCategorieJuridique;

        gtmEventv4({
          event_name: "comparatif_principal_clic_cta_demarrer",
          button_id: "demarrer-1",
        });

        if (
          !isCommandePublique(opportunite.value.entreprise, groupeActivitesTags, codeActiviteJuridique) &&
            canBeCommandePublique(opportunite.value.entreprise, groupeActivitesTags, codeActiviteJuridique)
        ) {
          opportunite.value.estCommandePublique = true;

          return router.push({ name: 'est-commande-publique' });
        }

        if (isCommandePublique(opportunite.value.entreprise, groupeActivitesTags, codeActiviteJuridique)) {
          opportunite.value.estCommandePublique = true;
        }

        return router.push({ name: 'energie' });
      })
      .catch(() => {
        router.push({ name: "fatalError" });
      });
  }
  if (isEntrepriseSuggeree.value === false) {
    router.push({ name: 'energie' });
  }
}

onMounted(() => {
  store.dispatch("getDepartements");
  vidangeEntrepriseOpportunite();
  gtmEventv4(
    getTrackingObject({
      nomEtape: route.meta.etape,
      sousEtape: getSousEtapeFromName(route.name),
      eventName: "comparatif_principal_page_vue_etape",
    }),
  );
  gtmEventv4(
    getTrackingObject({
      nomEtape: route.meta.etape,
      sousEtape: getSousEtapeFromName(route.name),
      eventName: "comparatif_principal_page_vue_question",
    }),
  );
});
</script>

<template>
    <div>
        <div class="my-4">
            <h1 class="titre-principal text-base md:text-2xl">
                Pour quelle entreprise souhaitez-vous comparer les offres d'énergie ?
            </h1>
            <p class="sous-titre">
                Opéra Energie propose des services de courtage en énergie pour les professionnels. <br>
                Pour pouvoir traiter au mieux votre demande, merci d'indiquer la Raison Sociale ou le SIREN de votre entreprise.
            </p>
        </div>
        <div id="raison_sociale_field">
            <TextInput
                required
                required-message=""
                id="raison_sociale_input"
                label="Raison sociale ou SIREN"
                v-model="dataRaisonSocialField.raisonSocialeEntreprise"
                :errors="errors"
                placeholder="Indiquez ici la Raison sociale ou le SIREN de votre entreprise"
                type="text"
                @input="handleInputRaisonSociale"
                @focus="onFocus"
                data-cy="input_raison_sociale"
            />
            <div
                v-if="suggestions.length !== 0 && dataRaisonSocialField.focused"
                class="mt-2 max-h-72 overflow-y-auto rounded border border-gray-200"
            >
                <fieldset>
                    <legend class="sr-only">
                        Veuillez sélectionner votre entreprise :
                    </legend>
                    <div class="-space-y-px rounded-md bg-white">
                        <label
                            v-for="(suggestion, index) in suggestions"
                            @click.prevent="selectionEntreprise(suggestion)"
                            data-cy="suggestion"
                            :key="index"
                            class="relative flex cursor-pointer rounded-tl-md rounded-tr-md border p-4 hover:bg-gray-100"
                        >
                            <input
                                :checked="isEntrepriseSelectionne(suggestion)"
                                type="radio"
                                name="raison-sociale"
                                class="radio-raison-sociale h-4 w-4 rounded-full"
                            />
                            <div class="ml-3 flex flex-col">
                                <span class="block text-sm font-medium">
                                    {{ suggestion.raisonSociale }}</span
                                >
                                <span class="block text-sm"
                                >SIREN : {{ suggestion.siren.libelle }} /
                                    NAF {{ suggestion.codeNaf }}</span
                                >
                            </div>
                        </label>
                    </div>
                </fieldset>
            </div>

            <div
                v-if="
                    suggestions.length === 0 &&
                    dataRaisonSocialField.focused &&
                    dataRaisonSocialField.raisonSocialeEntreprise.length > 3
                "
                class="mt-2 max-h-72 rounded border border-gray-200"
            >
                <div class="flex flex-col items-center p-4">
                    <i class="fal fa-info-circle" />
                    <p>
                        Malheureusement nous n'avons pas trouvé votre
                        entreprise, cela peut arriver dans le cas d'une création
                        récente.
                    </p>
                    <p>
                        Merci d'indiquer le numéro de département de votre
                        entreprise si celle-ci a été créée dans les deux
                        derniers mois.
                    </p>
                    <select
                        @input="handleSelectionDepartement"
                        name="suggestion-departement"
                        id="suggestion-departement"
                        class="my-2 block w-1/3 rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900"
                    >
                        <option
                            v-for="departement in departements"
                            :key="departement.numero"
                            :value="departement.numero"
                        >
                            {{
                                `${departement.numero || ""} - ${departement.nom || ""}`
                            }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="mt-4">
                <div
                    class="relative"
                    v-if="
                        opportunite.entreprise.codeNaf ||
                        opportunite.entreprise.siren
                    "
                >
                    <div
                        class="absolute inset-0 flex items-center"
                        aria-hidden="true"
                    >
                        <div class="w-full border-t border-gray-300" />
                    </div>
                    <div class="relative flex justify-center">
                        <span
                            class="bg-white px-3 text-lg font-medium text-gray-900"
                        >Votre sélection</span
                        >
                    </div>
                </div>

                <div class="my-2 flex flex-row justify-between text-sm">
                    <div v-if="opportunite.entreprise.siren">
                        <p class="libelle">Votre SIREN</p>
                        <p class="font-bold text-green-600" data-cy="siren">
                            {{ groupeDeTrois(opportunite.entreprise.siren) }}
                        </p>
                    </div>
                    <div v-if="opportunite.entreprise.codeNaf">
                        <p class="libelle">Votre code NAF</p>
                        <p class="font-bold text-green-600" data-cy="code_naf">
                            {{ opportunite.entreprise.codeNaf }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <div class="zoneActions">
            <bouton-action
                text="C'est parti !"
                class="boutton-principal"
                data-cy="demarrer"
                @click="demarrer"
            />
        </div>
    </div>
</template>
